import { useCallback } from 'react';

import { isApiError } from '@bookora/ui';
import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  guidanceRecord,
  guidanceReset,
  guidanceUpdateProfile,
  useGuidanceState,
} from '@/api/generated/guidance/guidance';
import type {
  GuidanceProfileResponse,
  GuidanceStateResponse,
  RecordGuidanceEventsDto,
  UpdateGuidanceProfileDto,
} from '@/api/generated/model';
import { queryKeys } from '@/api/query-keys';
import { useSessionStore } from '@/auth/token-store';
import { toast } from '@/lib/feedback';

import { buildEvent, type ClientGuidanceEvent, type QueuedEvent } from './events';
import { guidanceQueue } from './queue';
import { applyToState } from './state';
import { COPY } from './tours/copy';
import { useTourStore } from './tour-store';

export type { ClientGuidanceEvent } from './events';

/** The caller's guidance state (`GET /guidance`): clinic-scoped, fetched once, retried once. */
export function useGuidance(): UseQueryResult<GuidanceStateResponse> {
  const clinicId = useSessionStore((s) => s.activeClinicId);
  const signedIn = useSessionStore((s) => Boolean(s.accessToken && s.user));
  return useGuidanceState({
    query: {
      queryKey: queryKeys.guidance.state(clinicId),
      enabled: signedIn && Boolean(clinicId),
      staleTime: Number.POSITIVE_INFINITY,
      retry: 1,
    },
  }) as UseQueryResult<GuidanceStateResponse>;
}

function isImpersonating(queryClient: QueryClient | null): boolean {
  const session = useSessionStore.getState();
  if (session.impersonatedBy) return true;
  if (!queryClient) return false;
  const cached = queryClient.getQueryData<GuidanceStateResponse>(
    queryKeys.guidance.state(session.activeClinicId),
  );
  return cached?.readOnly === true;
}

function pausedNewly(
  before: GuidanceStateResponse,
  after: GuidanceStateResponse,
  now: Date,
): boolean {
  const was = before.profile.autoOfferPausedUntil;
  const is = after.profile.autoOfferPausedUntil;
  const active = (iso: string | null | undefined): boolean =>
    Boolean(iso) && Date.parse(iso as string) > now.getTime();
  return !active(was) && active(is);
}

/**
 * Wires the one queue to this app's query client: responses land in the
 * guidance cache with every still-queued event folded back on top, so an
 * older server answer never undoes a newer optimistic write.
 */
export function configureGuidanceQueue(queryClient: QueryClient): void {
  guidanceQueue.configure({
    send: (events) => guidanceRecord({ events } as unknown as RecordGuidanceEventsDto),
    activeClinicId: () => useSessionStore.getState().activeClinicId,
    impersonating: () => isImpersonating(queryClient),
    onResponse: (clinicId, response, stillQueued) => {
      const now = new Date();
      const merged = stillQueued.reduce<GuidanceStateResponse>(
        (state, ev) => applyToState(state, ev, now),
        response,
      );
      queryClient.setQueryData(queryKeys.guidance.state(clinicId), merged);
    },
    isPermanentFailure: (error) =>
      isApiError(error) &&
      error.statusCode >= 400 &&
      error.statusCode < 500 &&
      error.statusCode !== 408 &&
      error.statusCode !== 429 &&
      error.statusCode !== 401,
  });
}

/**
 * Queued, fire-and-forget event recorder; a no-op while impersonating (the
 * server would write nothing either, and a support agent must never consume
 * a user's welcome). The event is folded into the cache first — the same
 * fold the server runs — so the UI moves on at once.
 */
export function useRecordGuidance(): (ev: ClientGuidanceEvent) => void {
  const queryClient = useQueryClient();
  return useCallback(
    (ev: ClientGuidanceEvent) => {
      if (isImpersonating(queryClient)) return;
      const clinicId = useSessionStore.getState().activeClinicId;
      if (!clinicId) return;
      const { viewport, persona } = useTourStore.getState();
      const now = new Date();
      const built: QueuedEvent = buildEvent(
        ev,
        { clinicId, viewport: viewport === 'compact' ? 'COMPACT' : 'WIDE', persona },
        now,
      );
      const key = queryKeys.guidance.state(clinicId);
      const before = queryClient.getQueryData<GuidanceStateResponse>(key);
      if (before) {
        const after = applyToState(before, built, now);
        queryClient.setQueryData(key, after);
        // Answers the user's own «نه، ممنون»: the second decline in a row
        // pauses offers for a week, and they should know it did.
        if (ev.kind === 'TOUR' && ev.type === 'OFFER_DECLINED' && pausedNewly(before, after, now)) {
          toast.info(COPY.toast.declinesPaused);
        }
      }
      guidanceQueue.enqueue(built);
    },
    [queryClient],
  );
}

/** `PATCH /guidance/profile`, applied optimistically and rolled back on failure. */
export function useUpdateGuidanceProfile(): UseMutationResult<
  GuidanceProfileResponse,
  unknown,
  UpdateGuidanceProfileDto,
  { previous: GuidanceStateResponse | undefined }
> {
  const queryClient = useQueryClient();
  const clinicId = useSessionStore((s) => s.activeClinicId);
  const key = queryKeys.guidance.state(clinicId);
  return useMutation({
    mutationFn: (dto: UpdateGuidanceProfileDto) => guidanceUpdateProfile(dto),
    onMutate: async (dto) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<GuidanceStateResponse>(key);
      if (previous && dto.autoOfferTours !== undefined) {
        queryClient.setQueryData<GuidanceStateResponse>(key, {
          ...previous,
          profile: { ...previous.profile, autoOfferTours: dto.autoOfferTours },
        });
      }
      return { previous };
    },
    onSuccess: (profile) => {
      queryClient.setQueryData<GuidanceStateResponse>(key, (current) =>
        current ? { ...current, profile } : current,
      );
    },
    onError: (error, _dto, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      toast.error(error);
    },
  });
}

/** `POST /guidance/reset` for the caller's own welcome and tours. */
export function useResetGuidance(): UseMutationResult<GuidanceStateResponse, unknown, void> {
  const queryClient = useQueryClient();
  const clinicId = useSessionStore((s) => s.activeClinicId);
  return useMutation({
    mutationFn: () => guidanceReset({ kinds: ['WELCOME', 'TOUR'] }),
    onSuccess: (state) => {
      queryClient.setQueryData(queryKeys.guidance.state(clinicId), state);
    },
  });
}
